// Route: /glossary — глобальный (агрегированный) глоссарий по всем материалам
// пользователя. READ — PowerSync (`useGlossaryList` / `useGlossaryMaterialGroups`);
// mutations (update/delete/export/quiz/generate) остаются на tRPC.
// Per-material глоссарий — workspace (`components/materials/glossary-tab.tsx`).
//
// Два режима отображения (переключатель «По материалам / Список»):
//   • byMaterial (дефолт) — термины сгруппированы в аккордеоны по материалам;
//   • list — плоский список (исторический).
// Опции фильтра «материал» + заголовки групп — из локальной SQLite (JOIN materials).

import type { GlossaryTerm } from "@studyhub/shared/contracts/glossary";
import { SegmentedControl } from "@studyhub/ui/components/segmented-control";
import { SectionHeader } from "@studyhub/ui/components/studyhub/section-header";
import { toast } from "@studyhub/ui/toast";
import { IconBook, IconLayoutList, IconList } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlossaryList, useGlossaryMaterialGroups } from "@/components/glossary/_api";
import { AddTermDialog } from "@/components/glossary/add-term-dialog";
import { GenerateGlossaryDialog } from "@/components/glossary/generate-glossary-dialog";
import {
	GlossaryFilterSelects,
	type ImportanceFilter,
	type StatusFilter,
} from "@/components/glossary/glossary-filters";
import {
	GlossaryDeleteDialog,
	GlossaryHeaderActions,
	GlossaryMainPanel,
	GlossaryNameInputDialog,
	GlossarySelectionBar,
} from "@/components/glossary/glossary-page-panels";
import {
	type GlossaryEditDraft,
	makeGlossaryEditDraft,
} from "@/components/glossary/glossary-term-card";
import type { GlossaryRowController } from "@/components/glossary/glossary-term-row";
import type { GlossaryViewMode } from "@/components/glossary/glossary-terms-view";
import { SECTION_TINT } from "@/components/navigation/section-icons";
import { LIST_PAGE_SHELL, ListToolbar } from "@/components/shared/entity-list";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { markArtifactSeenKey, useMarkArtifactSeen } from "@/hooks/use-mark-artifact-seen";
import { useRequireOnline } from "@/hooks/use-require-online";
import { useSocketEvent } from "@/hooks/use-socket-event";
import { trackGenerationAccepted } from "@/lib/ecosystem-analytics";
import { api } from "@/lib/trpc-client";

type SortBy = "alphabetical" | "importance" | "occurrences";

const VIEW_STORAGE_KEY = "studyhub:glossary-view";

function readStoredView(): GlossaryViewMode {
	try {
		const v = localStorage.getItem(VIEW_STORAGE_KEY);
		return v === "list" || v === "byMaterial" ? v : "byMaterial";
	} catch {
		return "byMaterial";
	}
}

export const Route = createFileRoute("/glossary")({
	component: GlossaryPage,
	pendingComponent: PagePendingFallback,
});

function GlossaryPage(): React.ReactElement {
	const { t } = useTranslation("glossary");
	const intro = useSectionIntro("glossary");
	const { online, guard } = useRequireOnline();
	const navigate = useNavigate();
	const markSeen = useMarkArtifactSeen();

	const [search, setSearch] = useState("");
	const [debounced, setDebounced] = useState("");
	const [sortBy, setSortBy] = useState<SortBy>("alphabetical");
	const [viewMode, setViewMode] = useState<GlossaryViewMode>(readStoredView);
	const [materialFilter, setMaterialFilter] = useState<string>("all");
	const [importanceFilter, setImportanceFilter] = useState<ImportanceFilter>("all");
	const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
	useArtifactWatching(
		materialFilter !== "all" && materialFilter !== "manual"
			? markArtifactSeenKey("glossary", materialFilter)
			: null,
	);

	// UI-состояние карточек
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [draft, setDraft] = useState<GlossaryEditDraft | null>(null);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
	const [exportOpen, setExportOpen] = useState(false);
	const [exportName, setExportName] = useState("");
	const [quizOpen, setQuizOpen] = useState(false);
	const [quizName, setQuizName] = useState("");
	const [addOpen, setAddOpen] = useState(false);
	const [generateOpen, setGenerateOpen] = useState(false);

	useEffect(() => {
		const handle = window.setTimeout(() => setDebounced(search.trim()), 250);
		return () => window.clearTimeout(handle);
	}, [search]);

	function changeViewMode(next: GlossaryViewMode): void {
		setViewMode(next);
		try {
			localStorage.setItem(VIEW_STORAGE_KEY, next);
		} catch {
			// localStorage недоступен (private mode) — режим живёт только в памяти.
		}
	}

	// READ — PowerSync (реактивно при sync после Inngest generateGlossary).
	const query = useGlossaryList({
		sortBy,
		...(debounced.length > 0 ? { search: debounced } : {}),
		...(materialFilter !== "all" ? { materialId: materialFilter } : {}),
	});
	const materialsQuery = useGlossaryMaterialGroups();
	const materialGroups = materialsQuery.data ?? [];

	// Surface-ack при готовности глоссария (строки подтянет PowerSync sync).
	useSocketEvent("glossary:ready", (payload) => {
		if (materialFilter !== "all" && payload.materialId === materialFilter) {
			markSeen(markArtifactSeenKey("glossary", payload.materialId), "surface");
		}
	});

	const updateTerm = api.glossary.update.useMutation({
		onSuccess: () => {
			toast.success(t("editSaved"));
			setEditingId(null);
			setDraft(null);
		},
		onError: () => toast.error(t("editFailed")),
	});

	const deleteTerm = api.glossary.delete.useMutation({
		onSuccess: () => {
			toast.success(t("deleteSuccess"));
			setDeleteTargetId(null);
		},
		onError: () => toast.error(t("deleteFailed")),
	});

	const exportToFlashcards = api.glossary.exportToFlashcards.useMutation({
		onSuccess: (res) => {
			toast.success(t("exportDone", { count: res.createdCount }));
			setSelected(new Set());
			setExportOpen(false);
			setExportName("");
		},
		onError: () => toast.error(t("exportFailed")),
	});

	// Генерация теста асинхронна (Inngest): возвращает placeholder-quizId сразу.
	// Навигируем на страницу редактора теста — она показывает «генерируется» и
	// снимает спиннер по Socket.IO `quiz:ready`.
	const generateQuiz = api.glossary.generateQuizFromTerms.useMutation({
		onSuccess: (res) => {
			trackGenerationAccepted("test", res);
			toast.success(t("quizQueued"));
			setSelected(new Set());
			setQuizOpen(false);
			setQuizName("");
			void navigate({ to: "/tests/edit/$id", params: { id: res.quizId } });
		},
		onError: () => toast.error(t("quizFailed")),
	});

	const rawTerms = query.data?.items ?? [];

	// Локальная фильтрация по importance/status — серверный endpoint их не принимает.
	const terms = useMemo(() => {
		return rawTerms.filter((tr) => {
			if (importanceFilter !== "all" && tr.importance !== importanceFilter) return false;
			if (statusFilter !== "all") {
				if (statusFilter === "user-created" && tr.status !== "manual" && tr.status !== "edited")
					return false;
				if (statusFilter === "ai-generated" && tr.status !== "ai-generated") return false;
				if (statusFilter === "verified" && tr.status !== "verified") return false;
			}
			return true;
		});
	}, [rawTerms, importanceFilter, statusFilter]);

	const total = terms.length;
	const hasFilters =
		importanceFilter !== "all" || statusFilter !== "all" || materialFilter !== "all" || !!debounced;
	const hasSearch = debounced.length > 0;
	const activeFilterCount =
		(materialFilter !== "all" ? 1 : 0) +
		(statusFilter !== "all" ? 1 : 0) +
		(importanceFilter !== "all" ? 1 : 0);

	const targetTerm = deleteTargetId ? rawTerms.find((tr) => tr.id === deleteTargetId) : undefined;

	function startEdit(term: GlossaryTerm): void {
		setEditingId(term.id);
		setExpandedId(term.id);
		setDraft(makeGlossaryEditDraft(term));
	}

	function cancelEdit(): void {
		setEditingId(null);
		setDraft(null);
	}

	function saveEdit(id: string): void {
		if (!draft || updateTerm.isPending) return;
		const term = draft.term.trim();
		const definition = draft.definition.trim();
		if (!term || !definition) return;
		const examples = draft.examples.map((e) => e.trim()).filter(Boolean);
		updateTerm.mutate({ id, term, definition, examples });
	}

	function toggleSelected(id: string): void {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	function handleExportSubmit(): void {
		if (!guard()) return;
		const name = exportName.trim() || t("exportSetName");
		if (selected.size === 0 || exportToFlashcards.isPending) return;
		exportToFlashcards.mutate({ termIds: [...selected], setName: name });
	}

	function handleQuizSubmit(): void {
		if (!guard()) return;
		const name = quizName.trim() || t("quizName");
		if (selected.size === 0 || generateQuiz.isPending) return;
		generateQuiz.mutate({ termIds: [...selected], quizName: name });
	}

	const controller: GlossaryRowController = {
		expandedId,
		editingId,
		draft,
		selected,
		editPending: updateTerm.isPending,
		onToggleExpand: (id) => setExpandedId((prev) => (prev === id ? null : id)),
		onSelectChange: toggleSelected,
		onEditDraftChange: setDraft,
		onEditStart: startEdit,
		onEditSave: saveEdit,
		onEditCancel: cancelEdit,
		onDelete: (id) => setDeleteTargetId(id),
	};

	return (
		<div className={LIST_PAGE_SHELL}>
			<SectionHeader
				icon={<IconBook />}
				category="ai"
				tint={SECTION_TINT.glossary}
				title={t("title")}
				description={t("subtitle")}
				// Ссылка-возврат плюс кнопки — уже широкий блок: без переноса он
				// съедал бы заголовок на 360px (проверено скриншотом песочницы).
				stackActions
				actions={
					<>
						<SectionIntroReopen intro={intro} />
						<GlossaryHeaderActions
							termCount={!query.isLoading && total > 0 ? total : null}
							onAdd={() => setAddOpen(true)}
							onGenerate={() => setGenerateOpen(true)}
						/>
					</>
				}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля,
			    состояние «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			{/* Единый тулбар. Режим (По материалам/Список) — сегменты; сортировка
			    и фильтры материал/статус/важность — в доп.фильтрах. Своего grid/
			    list-вида карточек у глоссария нет → showViewToggle отключён. */}
			<ListToolbar
				search={{ value: search, onChange: setSearch, placeholder: t("search") }}
				showViewToggle={false}
				segments={
					<SegmentedControl
						scrollable
						value={viewMode}
						onValueChange={(v) => changeViewMode(v as GlossaryViewMode)}
						options={[
							{
								value: "byMaterial",
								label: t("view.byMaterial"),
								icon: <IconLayoutList size={16} stroke={1.75} />,
							},
							{ value: "list", label: t("view.list"), icon: <IconList size={16} stroke={1.75} /> },
						]}
					/>
				}
				filtersActive={activeFilterCount > 0 || sortBy !== "alphabetical"}
				filters={
					<>
						<SegmentedControl
							scrollable
							value={sortBy}
							onValueChange={(v) => setSortBy(v as SortBy)}
							options={[
								{ value: "alphabetical", label: t("sort.alpha") },
								{ value: "importance", label: t("sort.importance") },
								{ value: "occurrences", label: t("sort.frequency") },
							]}
						/>
						<GlossaryFilterSelects
							materialFilter={materialFilter}
							onMaterialChange={setMaterialFilter}
							statusFilter={statusFilter}
							onStatusChange={setStatusFilter}
							importanceFilter={importanceFilter}
							onImportanceChange={setImportanceFilter}
							materials={materialGroups.map((g) => ({ id: g.materialId, name: g.name }))}
						/>
					</>
				}
				filtersSheet={
					<div className="flex flex-col gap-3">
						<SegmentedControl
							scrollable
							value={sortBy}
							onValueChange={(v) => setSortBy(v as SortBy)}
							options={[
								{ value: "alphabetical", label: t("sort.alpha") },
								{ value: "importance", label: t("sort.importance") },
								{ value: "occurrences", label: t("sort.frequency") },
							]}
						/>
						<GlossaryFilterSelects
							fullWidth
							materialFilter={materialFilter}
							onMaterialChange={setMaterialFilter}
							statusFilter={statusFilter}
							onStatusChange={setStatusFilter}
							importanceFilter={importanceFilter}
							onImportanceChange={setImportanceFilter}
							materials={materialGroups.map((g) => ({ id: g.materialId, name: g.name }))}
						/>
					</div>
				}
			/>

			<GlossarySelectionBar
				selectedCount={selected.size}
				quizPending={generateQuiz.isPending}
				exportPending={exportToFlashcards.isPending}
				onClear={() => setSelected(new Set())}
				onQuiz={() => setQuizOpen(true)}
				onExport={() => setExportOpen(true)}
			/>

			<GlossaryMainPanel
				isLoading={query.isLoading}
				isError={query.isError}
				total={total}
				hasFilters={hasFilters}
				hasSearch={hasSearch}
				viewMode={viewMode}
				terms={terms}
				materialGroups={materialGroups}
				controller={controller}
				onRetry={() => void query.refetch()}
				onAdd={() => setAddOpen(true)}
				onGenerate={() => {
					if (!guard()) return;
					setGenerateOpen(true);
				}}
			/>

			<GlossaryDeleteDialog
				open={!!deleteTargetId}
				termLabel={targetTerm?.term ?? ""}
				online={online}
				pending={deleteTerm.isPending}
				onOpenChange={(open) => {
					if (!open) setDeleteTargetId(null);
				}}
				onConfirm={() => {
					if (deleteTargetId) deleteTerm.mutate({ id: deleteTargetId });
				}}
			/>

			{/* Export to flashcards dialog */}
			<GlossaryNameInputDialog
				open={exportOpen}
				onOpenChange={setExportOpen}
				title={t("exportToFlashcards")}
				value={exportName}
				onValueChange={setExportName}
				placeholder={t("exportSetName")}
				cancelLabel={t("deleteCancel")}
				submitLabel={t("applyAll")}
				onSubmit={handleExportSubmit}
				pending={exportToFlashcards.isPending}
				disabled={selected.size === 0}
			/>

			{/* Generate quiz dialog */}
			<GlossaryNameInputDialog
				open={quizOpen}
				onOpenChange={setQuizOpen}
				title={t("generateQuizFromSelected")}
				description={t("quizDescription")}
				value={quizName}
				onValueChange={setQuizName}
				placeholder={t("quizName")}
				cancelLabel={t("deleteCancel")}
				submitLabel={t("quizGenerate")}
				onSubmit={handleQuizSubmit}
				pending={generateQuiz.isPending}
				disabled={selected.size === 0}
			/>

			{/* PowerSync подхватит create/generate через sync — invalidate не нужен. */}
			<AddTermDialog open={addOpen} onOpenChange={setAddOpen} />

			<GenerateGlossaryDialog open={generateOpen} onOpenChange={setGenerateOpen} />
		</div>
	);
}
